<template>
  <v-menu v-model="isBirthdayPickerOpen" location="bottom" :close-on-content-click="false">
    <template #activator="{ props: menuProps }">
      <v-text-field
        density="compact"
        variant="outlined"
        color="orange50"
        :focused="isBirthdayPickerOpen"
        :placeholder="placeholder"
        hide-details="auto"
        inputmode="numeric"
        maxlength="8"
        pattern="[0-9]*"
        :error-messages="errorMessage"
        class="wd-flex-grow-0 wd-w-full md:wd-w-1/2"
        v-bind="menuProps"
        :model-value="modelValue"
        @beforeinput="onBirthdayBeforeInput"
        @paste.prevent="onBirthdayPaste"
        @update:model-value="onBirthdayInput"
        @blur="onBirthdayBlur"
      >
        <template #append-inner>
          <FunnowIcon active-class="wd-w-5 wd-h-5" image-path="calendar-icon.svg" />
        </template>
      </v-text-field>
    </template>
    <v-date-picker
      v-model:view-mode="pickerViewMode"
      :model-value="selectedBirthdayDate"
      color="orange50"
      hide-header
      :min="birthdayConfig.min"
      :max="birthdayConfig.max"
      :month="birthdayPickerDisplayMonth"
      :year="birthdayPickerDisplayYear"
      width="335px"
      @update:model-value="onBirthdayDateSelected"
    />
  </v-menu>

  <MemberBirthdayConfirmModal
    v-model="isBirthdayConfirmOpen"
    :date="birthdayConfirmDateFormatted"
    @back="onConfirmBack"
    @confirm="onConfirmConfirm"
  />
</template>

<script setup lang="ts">
import type { Dayjs } from 'dayjs'

import FunnowIcon from '@/components/icons/funnow-icon.vue'
import MemberBirthdayConfirmModal from '@/components/member/MemberBirthdayConfirmModal.vue'

import { useMemberBirthdayField } from '@/composables/useMemberBirthdayField'

import type { MemberFormValue } from '@/view-models/member'

interface Props {
  errorMessage?: string
  placeholder?: string
  birthdayDefaultView: Dayjs
  validate: () => void
}

const props = defineProps<Props>()

const modelValue = defineModel<MemberFormValue>({ required: true })

const isBirthdayPickerOpen = ref(false)
const isBirthdayConfirmOpen = ref(false)
const pickerViewMode = ref<'month' | 'months' | 'year'>('month')

const {
  birthdayConfig,
  birthdayPickerDisplayMonth,
  birthdayPickerDisplayYear,
  selectedBirthdayDate,
  normalizedBirthday,
  birthdayConfirmDateFormatted,
  onBirthdayDateSelected,
  onBirthdayInput,
  onBirthdayBeforeInput,
  onBirthdayPaste,
  onBirthdayBlur,
} = useMemberBirthdayField({
  draft: modelValue,
  birthdayDefaultView: props.birthdayDefaultView,
  closePicker: () => {
    isBirthdayPickerOpen.value = false
  },
  validate: props.validate,
})

let resolveConfirm: ((confirmed: boolean) => void) | null = null

function confirmBeforeSave(): Promise<boolean> {
  modelValue.value = normalizedBirthday.value
  isBirthdayConfirmOpen.value = true
  return new Promise(resolve => {
    resolveConfirm = resolve
  })
}

function onConfirmBack() {
  resolveConfirm?.(false)
  isBirthdayConfirmOpen.value = false
}

function onConfirmConfirm() {
  resolveConfirm?.(true)
  isBirthdayConfirmOpen.value = false
}

defineExpose({ confirmBeforeSave })
</script>

<style lang="scss" scoped>
/* 全域 index.scss 把 .v-picker__body 內的 icon btn 隱藏，覆寫讓生日 picker 的年份切換按鈕（▼）顯示回來 */
:deep(.v-picker__body .v-date-picker-controls > .v-btn--icon) {
  display: flex !important;
}
</style>
